import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import {
  EmailRequestType,
  LabelId,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import {
  useCommunicationIntentsStore,
  useUserCommunicationsStore,
} from '../../../store';
import { getLabelIdsForAction } from '../../../utils/emailLabelHelpers';
import { navigateToInboxOrDoBox } from '../../../utils/navigation';
import { useActiveInboxEntriesSlice } from '../../InboxEntries';
import { useCustomToast } from '../../useCustomToast';
import { useCancelSnoozeOnRemoval } from '../snooze';

export const useArchiveUserCommunication = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cancelSnoozeFor } = useCancelSnoozeOnRemoval();

  const {
    setArchiveLoading,
    archiveLoading,
    archiveUserCommunication,
    getUserCommunicationByMessageId,
    updateUserCommunicationLabels,
  } = useUserCommunicationsStore();
  const { setLabelsIntent, clearIntent, settleIntent } =
    useCommunicationIntentsStore();

  const { updateUserCommunicationLabelsInInboxEntries } =
    useActiveInboxEntriesSlice();

  const { errorMessage } = useCustomToast();

  const handleArchiveUserCommunication = useCallback(
    async (
      messageIds: string | string[],
      options?: { onSuccess?: () => void; shouldNavigate?: boolean },
    ) => {
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      const communicationsToArchive = messageIdsArray
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);
          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }
          return { messageId, communication: userCommunication };
        })
        .filter(
          (
            item,
          ): item is { messageId: string; communication: UserCommunication } =>
            item !== null,
        );

      if (communicationsToArchive.length === 0) {
        errorMessage(t('inbox.userCommunication.actions.archiveError'));
        setArchiveLoading(false);
        return;
      }

      setArchiveLoading(true);

      const labelBackupMap = new Map<string, LabelId[]>();

      communicationsToArchive.forEach(({ messageId, communication }) => {
        labelBackupMap.set(messageId, communication.labelIds || []);
        const expectedLabelIds = getLabelIdsForAction(
          communication.labelIds,
          'archive',
        );

        setLabelsIntent({
          messageId,
          threadMessageId: communication.threadMessageId,
          internetMessageId: communication.internetMessageId,
          originalLabelIds: communication.labelIds,
          expectedLabelIds,
        });
        updateUserCommunicationLabels(messageId, expectedLabelIds);
      });

      const revertOptimisticArchive = (messageIdsToRevert: string[]) => {
        messageIdsToRevert.forEach((messageId) => {
          const previousLabels = labelBackupMap.get(messageId);
          if (previousLabels) {
            updateUserCommunicationLabels(messageId, previousLabels);
          }
          clearIntent(messageId);
        });
      };

      try {
        const operations = communicationsToArchive.map(
          async ({ messageId, communication }) => {
            try {
              if (!communication.userEmailAddress) {
                throw new Error('No user email address found in communication');
              }

              if (!communication.platform) {
                throw new Error('No platform found in communication');
              }

              await modifyEmail({
                userEmailAddress: communication.userEmailAddress,
                platform: communication.platform,
                messageId: communication.messageId,
                action: EmailRequestType.ARCHIVE,
              });

              settleIntent(messageId);

              return { messageId, success: true, communication };
            } catch (error) {
              log.error(
                `Failed to archive email for messageId ${messageId}:`,
                error,
              );
              return { messageId, success: false, error, communication };
            }
          },
        );

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);

        if (failed.length === 0) {
          log.info(
            `${communicationsToArchive.length} messages archived successfully`,
          );

          communicationsToArchive.forEach(({ messageId }) => {
            archiveUserCommunication(messageId);
          });

          updateUserCommunicationLabelsInInboxEntries(
            messageIdsArray,
            (currentLabels) => getLabelIdsForAction(currentLabels, 'archive'),
          );

          cancelSnoozeFor(
            communicationsToArchive.map(({ messageId }) => messageId),
          );

          options?.onSuccess?.();

          if (options?.shouldNavigate) {
            const currentTab = searchParams.get('tab');
            navigateToInboxOrDoBox(navigate, currentTab);
          }
        } else {
          log.warn(
            `${failed.length} operations failed out of ${communicationsToArchive.length}`,
          );

          revertOptimisticArchive(failed.map((result) => result.messageId));

          errorMessage(
            failed.length === communicationsToArchive.length
              ? t('inbox.userCommunication.actions.archiveError')
              : t('inbox.userCommunication.actions.partialArchiveError', {
                  failed: failed.length,
                  total: communicationsToArchive.length,
                }),
          );
        }
      } catch (error) {
        log.error(error);
        revertOptimisticArchive(messageIdsArray);
        errorMessage(t('inbox.userCommunication.actions.archiveError'));
      } finally {
        setArchiveLoading(false);
      }
    },
    [
      archiveUserCommunication,
      updateUserCommunicationLabelsInInboxEntries,
      setArchiveLoading,
      getUserCommunicationByMessageId,
      updateUserCommunicationLabels,
      setLabelsIntent,
      clearIntent,
      settleIntent,
      errorMessage,
      navigate,
      searchParams,
      t,
      cancelSnoozeFor,
    ],
  );

  const extractMessageIdFromSelectedItems = useCallback(
    (selectedItems: Set<string>) => {
      return Array.from(selectedItems).map((messageId) => messageId);
    },
    [],
  );

  return {
    handleArchiveUserCommunication,
    extractMessageIdFromSelectedItems,
    isArchiveLoading: archiveLoading,
  };
};
